import React, { useMemo, useState } from "react";
import {
  Box,
  Fab,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Divider,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BackspaceIcon from "@mui/icons-material/Backspace";

const IVA_OPTIONS = [
  { value: 5, label: "5%" },
  { value: 15, label: "15%" }
];

const quickPrices = ["0.15", "0.33", "0.75", "1.00", "2.67"];

const keypad = [
  "7",
  "8",
  "9",
  "/",
  "4",
  "5",
  "6",
  "*",
  "1",
  "2",
  "3",
  "-",
  "0",
  ".",
  "=",
  "+"
];

const normalizeDecimal = (value) => {
  let s = String(value ?? "");
  s = s.replace(/,/g, ".");
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
};

const normalizeExpression = (value) => {
  let s = String(value ?? "");
  s = s.replace(/,/g, ".");
  s = s.replace(/×/g, "*");
  s = s.replace(/÷/g, "/");
  s = s.replace(/[^0-9+\-*/().% ]/g, "");
  return s;
};

const round6 = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toFixed(6) : "0.000000";
};

const round2 = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const safeCalculate = (expression) => {
  const clean = normalizeExpression(expression);
  if (!clean.trim()) return "";
  if (!/^[0-9+\-*/().% ]+$/.test(clean)) return "Error";
  try {
    const result = Function(`"use strict"; return (${clean})`)();
    if (!Number.isFinite(result)) return "Error";
    return String(Number(result.toFixed(12)));
  } catch {
    return "Error";
  }
};

export default function Calculator() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("normal");
  const [finalPrice, setFinalPrice] = useState("");
  const [iva, setIva] = useState(15);
  const [expression, setExpression] = useState("");
  const [normalResult, setNormalResult] = useState("");
  const [copied, setCopied] = useState(false);

  const factor = 1 + Number(iva || 0) / 100;

  const priceResult = useMemo(() => {
    const total = Number(normalizeDecimal(finalPrice)) || 0;
    const base = factor === 0 ? 0 : total / factor;
    const tax = total - base;

    return {
      total,
      base,
      tax,
      base6: round6(base),
      base2: round2(base),
      tax2: round2(tax),
      total2: round2(total)
    };
  }, [finalPrice, factor]);

  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
    } catch {
      setCopied(true);
    }
  };

  const pressKey = (key) => {
    if (key === "=") {
      setNormalResult(safeCalculate(expression));
      return;
    }

    setExpression((prev) => normalizeExpression(`${prev}${key}`));
  };

  const clearNormal = () => {
    setExpression("");
    setNormalResult("");
  };

  const backspaceNormal = () => {
    setExpression((prev) => prev.slice(0, -1));
  };

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 1600
        }}
      >
        {open && (
          <Paper
            elevation={10}
            sx={{
              position: "absolute",
              right: 0,
              bottom: 78,
              width: { xs: 310, sm: 380 },
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 4,
              overflow: "hidden",
              background: "linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%)",
              boxShadow: "0 22px 55px rgba(7,28,53,0.25)"
            }}
          >
            <Box
              sx={{
                px: 2.4,
                py: 1.8,
                bgcolor: "#0D47A1",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <Typography variant="h6" fontWeight={900}>
                Calculadora
              </Typography>

              <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 2.4 }}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, value) => {
                  if (value) setMode(value);
                }}
                fullWidth
                sx={{
                  mb: 2.2,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: "14px !important",
                    py: 1
                  }
                }}
              >
                <ToggleButton value="price">Precio Final</ToggleButton>
                <ToggleButton value="normal">Normal</ToggleButton>
              </ToggleButtonGroup>

              {mode === "price" ? (
                <Stack spacing={2}>
                  <TextField
                    label="Precio final inc IVA"
                    value={finalPrice}
                    onChange={(e) => setFinalPrice(normalizeDecimal(e.target.value))}
                    fullWidth
                    inputProps={{ inputMode: "decimal" }}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ mr: 1, fontWeight: 900, color: "#1565C0" }}>
                          $
                        </Typography>
                      )
                    }}
                  />

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.8,
                        color: "#475569",
                        fontWeight: 800
                      }}
                    >
                      IVA
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {IVA_OPTIONS.map((item) => (
                        <Chip
                          key={item.value}
                          label={item.label}
                          clickable
                          onClick={() => setIva(item.value)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 900,
                            minWidth: 72,
                            bgcolor: iva === item.value ? "#1565C0" : "#edf4ff",
                            color: iva === item.value ? "#fff" : "#0D47A1",
                            "&:hover": {
                              bgcolor: iva === item.value ? "#0D47A1" : "#dcecff"
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.8,
                        color: "#475569",
                        fontWeight: 800
                      }}
                    >
                      Precio Ejemplo
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {quickPrices.map((price) => (
                        <Chip
                          key={price}
                          label={`$${price}`}
                          clickable
                          onClick={() => setFinalPrice(price)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 800,
                            bgcolor: finalPrice === price ? "#1565C0" : "#edf4ff",
                            color: finalPrice === price ? "#fff" : "#0D47A1",
                            "&:hover": {
                              bgcolor: finalPrice === price ? "#0D47A1" : "#dcecff"
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      bgcolor: "#071C35",
                      color: "#fff"
                    }}
                  >
                    <Stack spacing={1.4}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>
                          Precio sin IVA para guardar
                        </Typography>

                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography variant="h4" fontWeight={950}>
                            ${priceResult.base6}
                          </Typography>

                          <IconButton onClick={() => copyValue(priceResult.base6)} sx={{ color: "#fff" }}>
                            <ContentCopyIcon />
                          </IconButton>
                        </Stack>
                      </Box>

                      <Divider sx={{ borderColor: "rgba(255,255,255,0.14)" }} />

                      <Stack direction="row" spacing={1.2}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>
                            Base factura
                          </Typography>
                          <Typography fontWeight={900}>${priceResult.base2}</Typography>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>
                            IVA factura
                          </Typography>
                          <Typography fontWeight={900}>${priceResult.tax2}</Typography>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>
                            Total cliente
                          </Typography>
                          <Typography fontWeight={900}>${priceResult.total2}</Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Paper>
                </Stack>
              ) : (
                <Stack spacing={1.6}>
                  <TextField
                    label="Operación"
                    value={expression}
                    onChange={(e) => setExpression(normalizeExpression(e.target.value))}
                    fullWidth
                    inputProps={{ inputMode: "decimal" }}
                  />

                  <Box
                    sx={{
                      p: 1.6,
                      borderRadius: 3,
                      bgcolor: "#071C35",
                      color: "#fff",
                      minHeight: 62
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.68)" }}>
                      Resultado
                    </Typography>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography variant="h5" fontWeight={950}>
                        {normalResult || "0"}
                      </Typography>

                      <IconButton
                        onClick={() => copyValue(normalResult)}
                        disabled={!normalResult || normalResult === "Error"}
                        sx={{
                          color: "#fff",
                          opacity: !normalResult || normalResult === "Error" ? 0.4 : 1
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      gap: 0.8
                    }}
                  >
                    {keypad.map((key) => (
                      <Button
                        key={key}
                        variant={key === "=" ? "contained" : "outlined"}
                        onClick={() => pressKey(key)}
                        sx={{
                          height: 38,
                          minWidth: 0,
                          borderRadius: 2.2,
                          fontWeight: 900,
                          fontSize: 15,
                          bgcolor: key === "=" ? "#1565C0" : undefined,
                          "&:hover": {
                            bgcolor: key === "=" ? "#0D47A1" : undefined
                          }
                        }}
                      >
                        {key === "*" ? "×" : key === "/" ? "÷" : key}
                      </Button>
                    ))}
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={clearNormal}
                      sx={{
                        height: 40,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 900
                      }}
                    >
                      Limpiar
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<BackspaceIcon />}
                      onClick={backspaceNormal}
                      sx={{
                        height: 40,
                        borderRadius: 2.5,
                        textTransform: "none",
                        fontWeight: 900
                      }}
                    >
                      Borrar
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Paper>
        )}

        <Fab
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            width: 62,
            height: 62,
            bgcolor: "#1565C0",
            color: "#fff",
            boxShadow: "0 18px 40px rgba(21,101,192,0.38)",
            "&:hover": {
              bgcolor: "#0D47A1",
              transform: "scale(1.05)"
            },
            transition: "all 0.25s ease"
          }}
        >
          {open ? <CloseIcon /> : <CalculateIcon />}
        </Fab>
      </Box>

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCopied(false)}
          sx={{
            bgcolor: "#E8F5E9",
            fontWeight: 800,
            color: "#2E7D32",
            "& .MuiAlert-icon": {
              color: "#2E7D32"
            }
          }}
        >
          Copiado
        </Alert>
      </Snackbar>
    </>
  );
}